import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import Button from "@/components/Base/Button";
import * as XLSX from 'xlsx';

type ImportStatus = {
    row: number;
    name: string;
    status: 'pending' | 'validating' | 'success' | 'warning' | 'error';
    message: string;
};

function OutletsImportPage() {
    const [file, setFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState<ImportStatus[]>([]);
    const [summary, setSummary] = useState({ total: 0, success: 0, failed: 0, warnings: 0 });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const companyId = searchParams.get("companyId");
    const projectId = searchParams.get("projectId");

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setImportResults([]);
            setSummary({ total: 0, success: 0, failed: 0, warnings: 0 });
        }
    };

    const handleImport = async () => {
        if (!file) return;
        if (!companyId && !projectId) {
            alert("Company ID or Project ID is required. Please navigate from the proper page.");
            return;
        }

        // Get userId from JWT token
        let userId: string | null = null;
        try {
            const token = getToken();
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                userId = payload.userId || null;
            }
        } catch (e) {
            console.error("Failed to decode JWT token:", e);
            alert("Failed to get user information. Please login again.");
            return;
        }

        if (!userId) {
            alert("User ID not found. Please login again.");
            return;
        }

        setImporting(true);
        setImportResults([]);

        try {
            // Read Excel file
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

            if (jsonData.length === 0) {
                alert("Excel file is empty!");
                setImporting(false);
                return;
            }

            // Fetch validation data (outlet groups, types, categories, states)
            const [outletGroupsRes, outletTypesRes, outletCategoriesRes, statesRes] = await Promise.all([
                fetch(`${import.meta.env.VITE_API_URL}/outlet-groups?companyId=${companyId || ''}`, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }),
                fetch(`${import.meta.env.VITE_API_URL}/outlet-types?companyId=${companyId || ''}`, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }),
                fetch(`${import.meta.env.VITE_API_URL}/outlet-categories?companyId=${companyId || ''}`, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }),
                fetch(`${import.meta.env.VITE_API_URL}/states`, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                })
            ]);

            const outletGroups = (await outletGroupsRes.json())?.data || [];
            const outletTypes = (await outletTypesRes.json())?.data || [];
            const outletCategories = (await outletCategoriesRes.json())?.data || [];
            const statesData = (await statesRes.json())?.data || [];

            // Fetch ALL existing outlets to check for duplicates (with pagination)
            let allExistingOutlets: any[] = [];
            let page = 1;
            let hasMore = true;
            const pageSize = 1000;
            
            while (hasMore) {
                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (projectId) queryParams.append("projectId", projectId);
                queryParams.append("pageSize", pageSize.toString());
                queryParams.append("page", page.toString());
                
                const existingOutletsRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/outlets?${queryParams.toString()}`,
                    { headers: { Authorization: `Bearer ${getToken()}` } }
                );
                const existingOutletsData = await existingOutletsRes.json();
                const pageOutlets = Array.isArray(existingOutletsData?.data) ? existingOutletsData.data : [];
                
                if (pageOutlets.length > 0) {
                    allExistingOutlets = [...allExistingOutlets, ...pageOutlets];
                    const totalPages = existingOutletsData.totalPages || 1;
                    hasMore = page < totalPages;
                    page++;
                } else {
                    hasMore = false;
                }
            }

            const existingOutlets = allExistingOutlets;

            // Create state name to ID mapping (case-insensitive)
            const stateNameToIdMap = new Map<string, string>();
            statesData.forEach((state: any) => {
                if (state?.name && state?.id) {
                    stateNameToIdMap.set(state.name.toLowerCase(), state.id);
                }
            });

            // Create a map of existing outlets by name (case-insensitive) for quick lookup
            const existingOutletMap = new Map<string, any>();
            existingOutlets.forEach((o: any) => {
                if (o?.name) {
                    existingOutletMap.set(o.name.toLowerCase(), o);
                }
            });

            const existingOutletNames = new Set(
                existingOutlets
                    .filter((o: any) => o?.name)
                    .map((o: any) => o.name.toLowerCase())
            );
            
            // Track outlet names in the Excel file to detect duplicates within the file
            const excelOutletNames = new Set<string>();
            const validGroups = new Set(
                outletGroups
                    .filter((g: any) => g?.groupName)
                    .map((g: any) => g.groupName.toLowerCase())
            );
            const validTypes = new Set(
                outletTypes
                    .filter((t: any) => t?.typeName)
                    .map((t: any) => t.typeName.toLowerCase())
            );
            const validCategories = new Set(
                outletCategories
                    .filter((c: any) => c?.catName)
                    .map((c: any) => c.catName.toLowerCase())
            );

            const results: ImportStatus[] = [];
            let successCount = 0;
            let failedCount = 0;
            let warningCount = 0;

            // Process each row
            for (let i = 0; i < jsonData.length; i++) {
                const row = jsonData[i];
                const rowNum = i + 2; // Excel row number (header is row 1)
                const name = row['Name']?.trim();

                let status: ImportStatus = {
                    row: rowNum,
                    name: name || 'N/A',
                    status: 'validating',
                    message: 'Validating...'
                };

                results.push(status);
                setImportResults([...results]);

                // Validation
                if (!name) {
                    status.status = 'error';
                    status.message = 'Outlet name is required';
                    failedCount++;
                    continue;
                }

                const nameLower = name.toLowerCase();

                // Check for duplicate in Excel file
                if (excelOutletNames.has(nameLower)) {
                    status.status = 'error';
                    status.message = 'Duplicate outlet name in Excel file';
                    failedCount++;
                    continue;
                }

                // Check for duplicate in existing outlets - we'll handle this after validation
                const existingOutlet = existingOutletMap.get(nameLower);

                // Add to Excel names set to track duplicates
                excelOutletNames.add(nameLower);

                // Validate outlet group
                const group = row['Group']?.trim();
                if (group && !validGroups.has(group.toLowerCase())) {
                    status.status = 'error';
                    status.message = `Outlet Group "${group}" not found or not match`;
                    failedCount++;
                    continue;
                }

                // Validate outlet type
                const type = row['Type']?.trim();
                if (type && !validTypes.has(type.toLowerCase())) {
                    status.status = 'error';
                    status.message = `Outlet Type "${type}" not found or not match`;
                    failedCount++;
                    continue;
                }

                // Validate outlet category
                const category = row['Category']?.trim();
                if (category && !validCategories.has(category.toLowerCase())) {
                    status.status = 'error';
                    status.message = `Outlet Category "${category}" not found or not match`;
                    failedCount++;
                    continue;
                }

                // Validate and convert state name to state ID
                const stateName = row['State']?.trim() || row['State Name']?.trim() || row['State ID']?.trim();
                let stateId: string | null = null;
                
                if (stateName) {
                    // Try to find state by name (case-insensitive)
                    const stateIdFromMap = stateNameToIdMap.get(stateName.toLowerCase());
                    if (stateIdFromMap) {
                        stateId = stateIdFromMap;
                    } else {
                        // Check if it's already a UUID (for backward compatibility)
                        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                        if (uuidRegex.test(stateName)) {
                            // It's a UUID, check if it exists in states
                            const stateExists = statesData.some((s: any) => s.id === stateName);
                            if (stateExists) {
                                stateId = stateName;
                            } else {
                                status.status = 'error';
                                status.message = `State ID "${stateName}" not found`;
                                failedCount++;
                                continue;
                            }
                        } else {
                            status.status = 'error';
                            status.message = `State "${stateName}" not found. Please use state name (e.g., "Melaka", "Selangor")`;
                            failedCount++;
                            continue;
                        }
                    }
                }

                // Convert GPS coordinates to numbers or null
                const gpsLatValue = row['GPS Lat'];
                const gpsLongValue = row['GPS Long'];
                const gpsLat = gpsLatValue !== null && gpsLatValue !== undefined && gpsLatValue !== '' 
                    ? (typeof gpsLatValue === 'string' ? parseFloat(gpsLatValue) : Number(gpsLatValue))
                    : null;
                const gpsLong = gpsLongValue !== null && gpsLongValue !== undefined && gpsLongValue !== '' 
                    ? (typeof gpsLongValue === 'string' ? parseFloat(gpsLongValue) : Number(gpsLongValue))
                    : null;

                // Validate GPS coordinates are valid numbers
                if (gpsLatValue && (isNaN(gpsLat as number) || gpsLat === null)) {
                    status.status = 'error';
                    status.message = `Invalid GPS Latitude value: ${gpsLatValue}`;
                    failedCount++;
                    continue;
                }
                if (gpsLongValue && (isNaN(gpsLong as number) || gpsLong === null)) {
                    status.status = 'error';
                    status.message = `Invalid GPS Longitude value: ${gpsLongValue}`;
                    failedCount++;
                    continue;
                }

                const outletData = {
                    name: name,
                    code: row['Code'] ? String(row['Code']) : '',
                    address: row['Address'] || '',
                    city: row['City'] || '',
                    postcode: row['Postcode'] ? String(row['Postcode']) : '',
                    stateId: stateId,
                    country: row['Country'] || '',
                    outletGroup: group || '',
                    outletType: type || '',
                    outletCategory: category || '',
                    contact: row['Contact'] ? String(row['Contact']) : '',
                    contactPIC: row['Outlet PIC'] || '',
                    imageUrl: row['Image Url'] || '',
                    gpsLat: gpsLat,
                    gpsLong: gpsLong,
                    // Automatically capture from user login information
                    companyId: companyId || '', // From URL params
                    ownerId: userId, // From logged-in user's JWT token
                    projectId: projectId || '', // From URL params
                    status: true // Always set to active
                };

                // Check if outlet name already exists in database
                if (existingOutlet) {
                    // Compare all fields to see if data is the same
                    const normalizeValue = (val: any): string => {
                        if (val === null || val === undefined || val === '') return '';
                        return String(val).trim().toLowerCase();
                    };

                    const normalizeNumber = (val: any): number | null => {
                        if (val === null || val === undefined || val === '') return null;
                        const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                        return isNaN(num) ? null : num;
                    };

                    const fieldsMatch = 
                        normalizeValue(existingOutlet.code) === normalizeValue(outletData.code) &&
                        normalizeValue(existingOutlet.address) === normalizeValue(outletData.address) &&
                        normalizeValue(existingOutlet.city) === normalizeValue(outletData.city) &&
                        normalizeValue(existingOutlet.postcode) === normalizeValue(outletData.postcode) &&
                        normalizeValue(existingOutlet.stateId) === normalizeValue(outletData.stateId) &&
                        normalizeValue(existingOutlet.country) === normalizeValue(outletData.country) &&
                        normalizeValue(existingOutlet.outletGroup) === normalizeValue(outletData.outletGroup) &&
                        normalizeValue(existingOutlet.outletType) === normalizeValue(outletData.outletType) &&
                        normalizeValue(existingOutlet.outletCategory) === normalizeValue(outletData.outletCategory) &&
                        normalizeValue(existingOutlet.contact) === normalizeValue(outletData.contact) &&
                        normalizeValue(existingOutlet.contactPIC) === normalizeValue(outletData.contactPIC) &&
                        normalizeValue(existingOutlet.imageUrl) === normalizeValue(outletData.imageUrl) &&
                        normalizeNumber(existingOutlet.gpsLat) === normalizeNumber(outletData.gpsLat) &&
                        normalizeNumber(existingOutlet.gpsLong) === normalizeNumber(outletData.gpsLong);

                    if (fieldsMatch) {
                        // All fields match - skip saving (no update needed)
                        status.status = 'warning';
                        status.message = 'Outlet already exists with identical data - skipped';
                        warningCount++;
                        // Add to sets to prevent duplicates within the same batch and Excel file
                        existingOutletNames.add(nameLower);
                        excelOutletNames.add(nameLower);
                        setImportResults([...results]);
                        continue;
                    } else {
                        // Fields differ - update existing outlet
                        try {
                            const response = await fetch(`${import.meta.env.VITE_API_URL}/outlets/${existingOutlet.id}`, {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${getToken()}`
                                },
                                body: JSON.stringify({
                                    ...outletData,
                                    status: true // Always set to active during import/update
                                })
                            });

                            if (response.ok) {
                                const updatedOutlet = await response.json();
                                status.status = 'success';
                                status.message = 'Successfully updated existing outlet';
                                successCount++;
                                // Update the map with the new data and add to sets
                                existingOutletMap.set(nameLower, updatedOutlet);
                                existingOutletNames.add(nameLower);
                                excelOutletNames.add(nameLower);
                            } else {
                                const error = await response.json();
                                status.status = 'error';
                                status.message = error?.error || 'Failed to update outlet';
                                failedCount++;
                            }
                        } catch (error) {
                            status.status = 'error';
                            status.message = 'Network error during update';
                            failedCount++;
                        }
                        setImportResults([...results]);
                        continue;
                    }
                }

                // No existing outlet - create new one
                try {
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/outlets`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${getToken()}`
                        },
                        body: JSON.stringify(outletData)
                    });

                    if (response.ok) {
                        const newOutlet = await response.json();
                        status.status = 'success';
                        status.message = 'Successfully imported';
                        successCount++;
                        // IMPORTANT: Add to sets and map to prevent duplicates within the same import batch and Excel file
                        existingOutletNames.add(nameLower);
                        excelOutletNames.add(nameLower);
                        existingOutletMap.set(nameLower, newOutlet);
                    } else {
                        const error = await response.json();
                        status.status = 'error';
                        status.message = error?.error || 'Failed to create outlet';
                        failedCount++;
                    }
                } catch (error) {
                    status.status = 'error';
                    status.message = 'Network error during import';
                    failedCount++;
                }

                setImportResults([...results]);
            }

            setSummary({
                total: jsonData.length,
                success: successCount,
                failed: failedCount,
                warnings: warningCount
            });

        } catch (error) {
            console.error('Import failed:', error);
            alert('Failed to process Excel file');
        } finally {
            setImporting(false);
        }
    };

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        {/* Header */}
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row mb-5">
                            <div className="text-base font-medium">
                                Import Outlets
                            </div>
                            <div className="flex gap-x-3 md:ml-auto">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                                    Back
                                </Button>
                            </div>
                        </div>

                        {/* Upload Section */}
                        <div className="box box--stacked p-5 mb-5">
                            <h3 className="font-medium text-lg mb-4">Select Excel File</h3>
                            <div className="flex items-center gap-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                >
                                    <Lucide icon="FileUp" className="w-4 h-4 mr-2" />
                                    Choose File
                                </Button>
                                {file && (
                                    <span className="text-sm text-slate-600">
                                        {file.name}
                                    </span>
                                )}
                            </div>
                            
                            {file && (
                                <Button
                                    variant="primary"
                                    onClick={handleImport}
                                    disabled={importing}
                                    className="mt-4"
                                >
                                    {importing ? (
                                        <>
                                            <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <Lucide icon="Upload" className="w-4 h-4 mr-2" />
                                            Start Import
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                        {/* Summary */}
                        {summary.total > 0 && (
                            <div className="box box--stacked p-5 mb-5">
                                <h3 className="font-medium text-lg mb-4">Import Summary</h3>
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="p-4 bg-slate-100 rounded-lg">
                                        <div className="text-2xl font-bold">{summary.total}</div>
                                        <div className="text-sm text-slate-600">Total Rows</div>
                                    </div>
                                    <div className="p-4 bg-green-100 rounded-lg">
                                        <div className="text-2xl font-bold text-green-700">{summary.success}</div>
                                        <div className="text-sm text-green-600">Successful</div>
                                    </div>
                                    <div className="p-4 bg-yellow-100 rounded-lg">
                                        <div className="text-2xl font-bold text-yellow-700">{summary.warnings}</div>
                                        <div className="text-sm text-yellow-600">Warnings</div>
                                    </div>
                                    <div className="p-4 bg-red-100 rounded-lg">
                                        <div className="text-2xl font-bold text-red-700">{summary.failed}</div>
                                        <div className="text-sm text-red-600">Failed</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {importResults.length > 0 && (
                            <div className="box box--stacked">
                                <div className="p-5">
                                    <h3 className="font-medium text-lg mb-4">Import Progress</h3>
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {importResults.map((result, index) => (
                                            <div
                                                key={index}
                                                className={`p-3 rounded-lg flex items-center gap-3 ${
                                                    result.status === 'success' ? 'bg-green-50' :
                                                    result.status === 'warning' ? 'bg-yellow-50' :
                                                    result.status === 'error' ? 'bg-red-50' :
                                                    'bg-slate-50'
                                                }`}
                                            >
                                                {result.status === 'success' && (
                                                    <Lucide icon="CheckCircle" className="w-5 h-5 text-green-600" />
                                                )}
                                                {result.status === 'warning' && (
                                                    <Lucide icon="AlertCircle" className="w-5 h-5 text-yellow-600" />
                                                )}
                                                {result.status === 'error' && (
                                                    <Lucide icon="XCircle" className="w-5 h-5 text-red-600" />
                                                )}
                                                {result.status === 'validating' && (
                                                    <Lucide icon="Loader2" className="w-5 h-5 text-slate-600 animate-spin" />
                                                )}
                                                {result.status === 'pending' && (
                                                    <Lucide icon="Clock" className="w-5 h-5 text-slate-400" />
                                                )}
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm">
                                                        Row {result.row}: {result.name}
                                                    </div>
                                                    <div className="text-xs text-slate-600">
                                                        {result.message}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default OutletsImportPage;

